import Icon from 'components/Icon';
import type { ReviewCountPerGrade } from 'models/product';
import { cn, cnm } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

interface Props {
	className?: string;
	reviewCountPerGrade: ReviewCountPerGrade;
	totalReviews: number;
}

export default function RatingDetails({
	className,
	reviewCountPerGrade,
	totalReviews,
}: Props) {
	const { tPlural } = useI18n();
	const perGrade = [
		reviewCountPerGrade.grade5,
		reviewCountPerGrade.grade4,
		reviewCountPerGrade.grade3,
		reviewCountPerGrade.grade2,
		reviewCountPerGrade.grade1,
	].map((count, i) => ({
		count,
		rating: 5 - i,
		percentage:
			count === 0 || totalReviews === 0
				? 0
				: Math.round((count / totalReviews) * 100),
	}));

	// A min-width value to ensure the number of reviews per grade are displayed
	// at the same size. Without it a '123' next to the 5-star count and a '7'
	// next to the 1-star will have different widths and things will look uneven.
	// Get the largest number and base the index on its character count.
	const ratingCountMinWidthClass = [
		'min-w-[1.5em]',
		'min-w-[2em]',
		'min-w-[2.5em]',
		'min-w-[3em]',
		'min-w-[3.5em]',
	][String(Math.max(...perGrade.map(({ count }) => count))).length - 1];

	return (
		<div className={cn('flex flex-col gap-3', className)}>
			<p className="sr-only">
				{perGrade
					.map(({ count, percentage, rating }) =>
						[
							String(count),
							tPlural('product_review_stars_screen_reader_text', rating, {
								starCount: rating,
							}),
							count > 0 && `(${percentage} %)`,
						]
							.filter(Boolean)
							.join(' '),
					)
					.join(', ')}
			</p>
			<div aria-hidden="true">
				{perGrade.map(({ count, percentage, rating }) => (
					<p
						key={rating}
						className="flex h-8 items-center justify-center rounded-full"
					>
						{/* A 1 is less wide than a 5, set min-width to align stars */}
						<span className="inline-block min-w-[0.75em] text-center font-bold">
							{rating}
						</span>
						<Icon name="star" size={16} className="ml-1" />
						<span
							className={cnm(
								'relative block h-2 grow overflow-hidden rounded-full bg-disabled forced-colors-outline',
								// Less right margin for visual balance - the star icon will
								// be at the container edge while the number will have some
								// internal space due to min-width.
								'mr-4 ml-6',
							)}
						>
							{Boolean(percentage) && (
								<span
									className={cn(
										'block h-full bg-inverse forced-colors:bg-[CanvasText]',
										percentage < 3 ? 'rounded-xs' : 'rounded-full',
									)}
									style={{ width: `${percentage}%` }}
								/>
							)}
						</span>
						<span
							className={cn(
								'inline-block text-right',
								ratingCountMinWidthClass,
							)}
						>
							{count}
						</span>
					</p>
				))}
			</div>
		</div>
	);
}
RatingDetails.displayName = 'RatingDetails';
